'use client';

import { forwardRef } from 'react';
import type { ChangeEvent } from 'react';
import { ActionIcon, TextInput } from '@highlight-cp/core-ui';
import type { TextInputProps } from '@highlight-cp/core-ui';
import { useTranslation } from 'next-i18next/pages';
import { FiSearch, FiX } from 'react-icons/fi';

/**
 * Props for `SearchInput` — a clearable search field prefixed with a search
 * icon. The value is managed externally; pass a `string` value and an `onChange`
 * that receives the new string (leading whitespace is stripped for convenience).
 *
 * Default placeholder is `"Search"`; pass `placeholder` to override (e.g. for
 * i18n or longer copy). It fills its container by default — size it with the
 * `w`/`maw` style props.
 */
export type SearchInputProps = Omit<
  TextInputProps,
  'leftSection' | 'rightSection' | 'type' | 'onChange' | 'value'
> & {
  value: string;
  onChange: (value: string) => void;
};

/**
 * ### SearchInput
 *
 * @description
 * Clearable search field with a leading search icon. Value is controlled via
 * `value` + `onChange(value: string)`; a clear button appears whenever the value
 * is non-empty. Leading whitespace is stripped on input.
 *
 * @example
 * <SearchInput value={search} onChange={setSearch} />
 *
 * @param props - {@link SearchInputProps}
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onChange, placeholder, ...rest }, ref) => {
    const { t } = useTranslation();

    const handleClear = () => onChange('');

    return (
      <TextInput
        w="100%"
        {...rest}
        ref={ref}
        type="search"
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange(event.currentTarget.value.replace(/^\s+/, ''))
        }
        placeholder={placeholder ?? t('Search')}
        leftSection={<FiSearch aria-hidden="true" focusable="false" />}
        rightSection={
          value ? (
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              onClick={handleClear}
              aria-label={t('Clear search')}
            >
              <FiX />
            </ActionIcon>
          ) : null
        }
      />
    );
  },
);
SearchInput.displayName = 'SearchInput';
